<template>
	<div role="main" id="main-content" tabindex="0">
		<div id="qualifiedDividendIncome">
			<div class="breadcrumbs container">
				<Breadcrumbs :breadcrumbs="breadcrumbs" />
			</div>
			<div class="qdi-hero">
				<div class="box">
					<div class="col">
						<Hero
							:imageSrc="heroContent.imageSrc"
							:showPrintButton="heroContent.showPrintButton"
							:showShareButton="heroContent.showShareButton"
							:showBgOverlay="heroContent.showBgOverlay"
							:customBgOverlay="heroContent.customBgOverlay"
							:showGlowEffect="heroContent.showGlowEffect"
							:showShadowEffect="heroContent.showShadowEffect"
							:customShadowRGBColor="heroContent.customShadowRGBColor"
							:invertColorsLeft="heroContent.invertColorsLeft"
							:invertColorsRight="heroContent.invertColorsRight"
							:eyebrow="heroContent.eyebrow"
							:headline="heroContent.headline"
							:excerpt="heroContent.excerpt"
							:link="heroContent.ctaLink"
							:subtopicNav="heroContent.subtopicNav"
							:pullLeft="heroContent.pullLeft"
							:pullRight="heroContent.pullRight"
							:customGlowRGBColor="heroContent.customGlowRGBColor"
							:customGlowAlphaValue="heroContent.customGlowAlphaValue"
							:customShadowAlphaValue="heroContent.customShadowAlphaValue"
						/>
					</div>
				</div>
			</div>
			<div class="container">
				<div class="col">
					<div class="box">
						<TableSearch
							searchId="qdi-search"
							:tableData="fundTableData"
							@updateFilteredTableRows="filterTable"
							@updateSearchErrorState="updateSearchErrorState"
						/>
						<download-button
							class="lg-3 md-4 sm-16"
							:title="downloadContent.buttonText"
							:buttonText="downloadContent.buttonText"
							@downloadClicked="downloadQDI"
							v-if="!showSearchError"
						/>
					</div>
					<div v-if="showSearchError" class="search-error">
						<span v-html="searchContent.error.beforeSearchInput"></span>"{{ searchInput }}"<span
							v-html="searchContent.error.afterSearchInput"
						></span>
					</div>
					<div v-else>
						<qualified-dividend-income-table
							v-if="filteredTableData.length > 0"
							:fundData="filteredTableData"
							:tableHeaderContent="tableHeaderContent"
							:ytdQdiEstimatesYear="ytdQdiEstimatesYear"
							:yearEndQdiYear="yearEndQdiYear"
							:showYearToDateEstimates="shouldShowYearToDateEstimates"
						/>
						<div class="descriptions">
							<p class="p2">
								<span v-html="descriptionsContent.qdiTable.description" />
							</p>
							<p v-if="shouldShowYearToDateEstimates" class="p2">
								<span v-html="descriptionsContent.yearToDateEstimates.label" class="--bold" />
								<br /><br />
								<span v-html="descriptionsContent.yearToDateEstimates.description" />
							</p>
							<p class="p2">
								<span v-html="descriptionsContent.historicalYearEndData.label" class="--bold" />
								<br /><br />
								<span v-html="descriptionsContent.historicalYearEndData.description" />
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from "axios";
import Hero from "@vg-faset/ui/src/components/hero/Hero.vue";
import QualifiedDividendIncomeTable from "./QualifiedDividendIncomeTable.vue";
import TableSearch from "@/common/TableSearch.vue";
import { log } from "@/services";
import { createDownload, tripleDashNull, replaceEmDashHtmlEntity } from "@/services";
import DownloadButton from "@/common/DownloadButton.vue";
import adobeLaunchService from "../services/adobeLaunchService.js";
import { append } from "@vg-faset/core/js/filters/string/index";
import papa from "papaparse";
import { Breadcrumbs } from "@vg-faset/ui/src/components/breadcrumbs";
import { utilityService } from "@/services/utility.service.js";

export default {
	name: "QualifiedDividendIncome",
	components: {
		Hero,
		QualifiedDividendIncomeTable,
		TableSearch,
		DownloadButton,
		Breadcrumbs
	},
	data() {
		return {
			qualifiedDividendIncome: [],
			filteredTableData: [],
			ytdQdiEstimatesYear: "",
			yearEndQdiYear: "",
			showSearchError: false,
			searchInput: "",
			adobeTracking: {
				siteSection: "funds-tax",
				subPage: "qualified-dividend-income",
				is404: "N"
			}
		};
	},
	computed: {
		content() {
			return this.$store.state.content;
		},
		heroContent() {
			return this.content.heroSection;
		},
		searchContent() {
			return this.content.search;
		},
		downloadContent() {
			return this.content.download;
		},
		tableHeaderContent() {
			return this.content.tableHeader;
		},
		descriptionsContent() {
			return this.content.descriptions;
		},
		fundTableData() {
			return this.qualifiedDividendIncome;
		},
		breadcrumbsContent() {
			return this.content.breadcrumbs;
		},
		breadcrumbs() {
			return [
				{
					text: this.breadcrumbsContent[0].text,
					url: "/tax-center"
				},
				{
					text: this.breadcrumbsContent[1].text
				}
			];
		},
		shouldShowYearToDateEstimates() {
			return !this.content.suppressYearToDateEstimates;
		}
	},
	created() {
		axios
			.get("fundData/qualifiedDividendIncome")
			.then((results) => {
				if (results.data.funds.length > 0) {
					this.qualifiedDividendIncome = results.data.funds;
					this.ytdQdiEstimatesYear = results.data.ytdQdiEstimatesYear;
					this.yearEndQdiYear = results.data.yearEndQdiYear;
					this.filterTable(this.fundTableData);
				} else {
					log("There was an error getting Qualified Dividend Income data. Redirecting to the Unavailable page.");
					this.$router.push("unavailable");
				}
			})
			.catch({});

		setTimeout(() => {
			adobeLaunchService.trackPageLoad(
				this.adobeTracking.is404,
				this.adobeTracking.siteSection,
				this.adobeTracking.subPage
			);
		}, 0);
	},
	methods: {
		filterTable: function (filteredRows) {
			this.filteredTableData = filteredRows;
		},
		updateSearchErrorState: function (shouldShowSearchError, searchInput) {
			this.showSearchError = shouldShowSearchError;
			this.searchInput = searchInput;
		},
		downloadQDI: function () {
			let csvFileData = ["\uFEFF"];

			csvFileData.push(`${this.downloadContent.title}\n\n`);

			const csvFundData = papa.unparse(this.getDownloadData());
			csvFileData.push(csvFundData);
			csvFileData.push(this.getDownloadFootnotes());

			createDownload(this.downloadContent.fileName, csvFileData);
		},
		getDownloadData: function () {
			let downloadData = {
				fields: this.getDownloadHeaders(),
				data: []
			};

			for (const fund of this.filteredTableData) {
				let fundData = [];

				fundData.push(tripleDashNull(fund.name));
				fundData.push(tripleDashNull(fund.ticker));
				if (this.shouldShowYearToDateEstimates) {
					fundData.push(tripleDashNull(append(utilityService.toFixed(fund.yearToDateQDI, 2), "%")));
				}
				fundData.push(tripleDashNull(append(utilityService.toFixed(fund.priorYearQDIForDividends, 2), "%")));
				fundData.push(
					tripleDashNull(append(utilityService.toFixed(fund.priorYearQDIForShortTermCapitalGains, 2), "%"))
				);

				downloadData.data.push(fundData);
			}
			return downloadData;
		},
		getDownloadHeaders: function () {
			let labels = this.tableHeaderContent.labels;
			let headers = [];
			headers.push(labels.fundName);
			headers.push(labels.symbol);
			if (this.shouldShowYearToDateEstimates) {
				headers.push(`${this.ytdQdiEstimatesYear} ${labels.yearToDateQdiEstimates}`);
			}
			headers.push(`${this.yearEndQdiYear} ${labels.yearEndQdiFiguresForDividends}`);
			headers.push(`${this.yearEndQdiYear} ${labels.yearEndQdiFiguresForCapitalGains}`);

			return headers;
		},
		getDownloadFootnotes: function () {
			let footnotes = [];

			footnotes.push(`"${this.downloadContent.footnotes.qdiTable}"`);
			if (this.shouldShowYearToDateEstimates) {
				footnotes.push(
					`"${this.downloadContent.footnotes.yearToDateEstimatesLabel}"\n"${this.downloadContent.footnotes.yearToDateEstimates}"`
				);
			}
			footnotes.push(
				`"${this.downloadContent.footnotes.historicalYearEndDataLabel}"\n"${this.downloadContent.footnotes.historicalYearEndData}"`
			);
			footnotes.push(`"${this.downloadContent.footnotes.etfShares}"`);

			footnotes.forEach((footnote, index) => {
				footnotes[index] = replaceEmDashHtmlEntity(footnote);
			});

			return `\n\n${footnotes.join("\n\n")}`;
		}
	}
};
</script>

<style lang="scss">
#qualifiedDividendIncome {
	.container {
		@include to-lg {
			padding-bottom: 28px;
		}
		padding-bottom: 48px;
	}

	.qdi-hero {
		margin-bottom: 30px;

		@include from-md {
			.hero {
				max-height: 262px;

				.hero-text h1 {
					white-space: nowrap;
					margin: 35px 0 24px;
				}
			}
		}
	}

	.breadcrumbs {
		padding-bottom: 9px;
		padding-top: 9px;
	}

	.search-error {
		font-size: 24px;
		padding-bottom: 175px;
	}
}
</style>
